import React from 'react';
import dayjs from 'dayjs';
import type { SalesOrder, SalesOrderItem } from '@/services/salesOrder.service';
import { formatAmountInWordsPlain } from '@/utils/amountInWords';
import { EngineCommercialA4Print } from '@/components/print/EngineCommercialPrints';
import { formatMoney, PRINT_PLAIN_AMOUNT_PROFILE, formatDate } from '@/utils/format';

export interface PrintableSalesOrderProps {
  order: SalesOrder;
  revenueServiceTypeLabel?: string;
  companyInfo?: {
    name?: string;
    address?: string;
    phone?: string;
    email?: string;
    taxNumber?: string;
  };
  onClose?: () => void;
}

function amt(v: unknown): string {
  return formatMoney(v ?? 0, PRINT_PLAIN_AMOUNT_PROFILE);
}

/** Sales order print — shared pagination engine with restored commercial columns. */
export const PrintableSalesOrder: React.FC<PrintableSalesOrderProps> = ({
  order,
  revenueServiceTypeLabel,
  companyInfo,
  onClose,
}) => {
  const currency = order.currency || 'AED';
  const items = [...(order.items || [])].sort((a, b) => (a.line_number ?? 0) - (b.line_number ?? 0));
  const toNum = (v: any) => (v == null || v === '' ? 0 : Number(v));
  const orderDate = order.order_date ? formatDate(order.order_date) || dayjs(order.order_date).format('DD-MM-YYYY') : '';
  const requiredDate = order.required_date
    ? formatDate(order.required_date) || dayjs(order.required_date).format('DD-MM-YYYY')
    : '';
  const grandTotal = toNum(order.grand_total);
  const totalAfterDiscount = toNum(order.total_after_discount);
  const vatAmount = toNum(order.vat_amount);
  const headerVatPct =
    vatAmount > 0
      ? toNum(order.vat_percentage) ||
        (totalAfterDiscount > 0 ? Math.round((vatAmount / totalAfterDiscount) * 10000) / 100 : 5)
      : 0;

  return (
    <EngineCommercialA4Print
      docType="sales_order"
      title="CUSTOMER ORDER"
      documentNumber={order.sales_order_number || 'SO'}
      partyName={order.customer_name || ''}
      onClose={onClose}
      metaLines={[
        { label: 'Customer', value: order.customer_name ? `M/s. ${order.customer_name}` : '' },
        { label: 'Address', value: order.customer_address || '' },
        { label: 'Attn', value: order.customer_contact || '' },
        { label: 'SO No', value: order.sales_order_number || '' },
        { label: 'Date', value: orderDate },
        { label: 'Required Date', value: requiredDate },
        { label: 'TRN', value: companyInfo?.taxNumber?.replace(/^TRN:\s*/i, '') || '' },
        { label: 'Revenue service type', value: revenueServiceTypeLabel || '' },
      ].filter((m) => m.value)}
      columns={[
        { key: 'sno', label: 'No' },
        { key: 'description', label: 'Description' },
        { key: 'rev', label: 'Rev' },
        { key: 'qty', label: 'Qty', align: 'right' },
        { key: 'price', label: 'Price', align: 'right' },
        { key: 'gross', label: 'Gross Amount', align: 'right' },
        { key: 'vatPct', label: 'VAT %', align: 'right' },
        { key: 'vatAmt', label: 'VAT Amt', align: 'right' },
        { key: 'total', label: 'Total Amount', align: 'right' },
      ]}
      lines={items.map((line: SalesOrderItem, idx) => {
        const qty = toNum(line.ordered_quantity);
        const price = toNum(line.unit_price);
        const gross = toNum(line.net_amount) > 0 ? toNum(line.net_amount) : qty * price;
        const vatAmt = toNum(line.vat_amount);
        const vatPct = vatAmt > 0 ? toNum(line.vat_percentage) || headerVatPct || 5 : 0;
        const total = gross + vatAmt;
        return {
          lineNumber: line.line_number ?? idx + 1,
          description: line.item_description ?? '—',
          extraLines: line.notes ? [line.notes] : undefined,
          cells: {
            rev: line.revision ?? '—',
            qty: qty.toFixed(2),
            price: `${currency} ${amt(price)}`,
            gross: `${currency} ${amt(gross)}`,
            vatPct: String(vatPct),
            vatAmt: `${currency} ${amt(vatAmt)}`,
            total: `${currency} ${amt(total)}`,
          },
        };
      })}
      finals={[
        { id: 'totals', heightPx: 110 },
        { id: 'sign', heightPx: 64 },
      ]}
      finalBlocks={{
        totals: (
          <div>
            <p>
              <strong>
                Total Only : ({currency}) {formatAmountInWordsPlain(grandTotal)}
              </strong>
            </p>
            {order.payment_terms ? (
              <p>
                <strong>Payment Terms:</strong> {order.payment_terms}
              </p>
            ) : null}
            {order.delivery_terms ? (
              <p>
                <strong>Delivery Terms:</strong> {order.delivery_terms}
              </p>
            ) : null}
            {order.notes ? (
              <p>
                <strong>Notes:</strong> {order.notes}
              </p>
            ) : null}
            <table style={{ marginLeft: 'auto', fontSize: 12 }}>
              <tbody>
                <tr>
                  <td>Total Amount</td>
                  <td style={{ textAlign: 'right', paddingLeft: 16 }}>
                    {currency} {amt(totalAfterDiscount)}
                  </td>
                </tr>
                <tr>
                  <td>Net Taxable Amount</td>
                  <td style={{ textAlign: 'right', paddingLeft: 16 }}>
                    {currency} {amt(totalAfterDiscount)}
                  </td>
                </tr>
                <tr>
                  <td>VAT Amount {headerVatPct}%</td>
                  <td style={{ textAlign: 'right', paddingLeft: 16 }}>
                    {currency} {amt(vatAmount)}
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Grand Total</strong>
                  </td>
                  <td style={{ textAlign: 'right', paddingLeft: 16 }}>
                    <strong>
                      {currency} {amt(grandTotal)}
                    </strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ),
        sign: (
          <div>
            <p>Prepared order details for customer confirmation and execution.</p>
            <div style={{ display: 'flex', gap: 48, marginTop: 8 }}>
              <div>
                Prepared By Signature
                <br />
                _______________________
              </div>
              <div>
                Customer Signature
                <br />
                _______________________
              </div>
            </div>
          </div>
        ),
      }}
    />
  );
};

export default PrintableSalesOrder;
